import type { ShortsEntrySource } from './entrySource';
import type { ShortVideo } from '~/composables/shorts/useFetchShorts';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { useLastVideoState } from '~/composables/shorts/useLastVideoState';
import { usePropertyIntentPrefetch } from '~/composables/shorts/usePropertyIntentPrefetch';
import {
	useShortsEntryContext,
	useShortsEntrySeedItem
} from '~/composables/shorts/useShortsEntryContext';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { shortsFiltersToListingQuery } from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { markShortsOpenIntent } from '~/composables/useVideoQoE';
import { prefetchShakaChunkOnIdle } from '~/composables/useVideoWarmup';

import {
	blessSharedVideoElement,
	setIOSMsePreference
} from '~/components/PropertyReelsFeed/useIOSSharedVideo';

import { withEntrySource } from './entrySource';

export { SHORTS_ENTRY_SOURCE, type ShortsEntrySource } from './entrySource';

export interface OpenShortsFeedEntryContext {
	items: ShortVideo[];
	filters: ShortsFilters;
	page: number;
	perPage?: number;
	hasMore: boolean;
	allWatched?: boolean;
}

interface OpenShortsFeedParams {
	commercialId: string;
	clickedIndex: number;
	entryContext?: OpenShortsFeedEntryContext | null;
	/** O item clicado, para a página tocar sem esperar o payload. */
	entryItem?: ShortVideo | null;
	queryFilters?: ShortsFilters;
	queryParams?: Record<string, string>;
	entrySource?: ShortsEntrySource;
}

export function buildShortsFeedDestination(
	commercialId: string,
	query: Record<string, string> = {},
	entrySource?: ShortsEntrySource
): string {
	const search = new URLSearchParams(
		withEntrySource(query, entrySource)
	).toString();
	return `/shorts/${commercialId}${search ? `?${search}` : ''}`;
}

export function useOpenShortsFeed() {
	const lastVideoState = useLastVideoState();
	const shortsEntryContext = useShortsEntryContext();
	const shortsEntrySeedItem = useShortsEntrySeedItem();

	// Any surface that can open the shorts feed is a strong signal the Shaka
	// chunk will be needed — warm it while this page idles. The iOS MSE
	// preference must be set first: outside the shorts page it defaults to
	// false, which would misclassify MSE-capable iOS as native playback and
	// skip the warm for users who will in fact load Shaka.
	setIOSMsePreference(useFeatureFlags().ft_shorts_ios_mse);
	prefetchShakaChunkOnIdle();

	const { prefetchPropertyOnIntent } = usePropertyIntentPrefetch();

	function openShortsFeed({
		commercialId,
		clickedIndex,
		entryContext,
		entryItem,
		queryFilters,
		queryParams,
		entrySource
	}: OpenShortsFeedParams): void {
		const path = `/shorts/${commercialId}`;

		// User-felt TTFF starts at this click — the QoE vital video_ttff_click
		// measures from here to the first rendered frame in the feed.
		markShortsOpenIntent();

		// Confirmed intent: the reel page will fetch this exact property right
		// after the SPA navigation — start the request now so it runs in
		// parallel with the route transition.
		prefetchPropertyOnIntent(commercialId);

		// A cadeia vai junto: e o que da ao botao do header o primeiro segmento do
		// reel contextual sem request de descoberta.
		lastVideoState.save(
			commercialId,
			clickedIndex,
			entryItem?.videoUrl,
			entryItem?.prefetchVariants
		);
		blessSharedVideoElement();

		if (entryContext) {
			shortsEntryContext.value = entryContext;
		}
		shortsEntrySeedItem.value = entryItem ?? null;

		if (queryParams) {
			navigateTo({ path, query: withEntrySource(queryParams, entrySource) });
			return;
		}

		const filters = queryFilters ?? entryContext?.filters;
		if (!filters) {
			if (entrySource) {
				navigateTo({ path, query: withEntrySource({}, entrySource) });
			} else {
				navigateTo(path);
			}
			return;
		}

		navigateTo({
			path,
			query: withEntrySource(shortsFiltersToListingQuery(filters), entrySource)
		});
	}

	return { openShortsFeed };
}
